import React, { useEffect, useState } from 'react';
import { InlineWidget } from 'react-calendly';
import { useLocation, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import SharedButton from '../global/Button/Button';

// Extend Window interface for LinkedIn tracking
declare global {
  interface Window {
    _linkedin_data_partner_ids?: string[];
    lintrk?: {
      (action: string, data?: any): void;
      q?: any[];
    };
  }
}

const BookADemo = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const utmSource = searchParams.get('utm_source') || 'direct';

  const [isUpdating, setIsUpdating] = useState(false);
  const [updateStatus, setUpdateStatus] = useState<string | null>(null);
  const [isBookingCompleted, setIsBookingCompleted] = useState(false);

  useEffect(() => {
    if (utmSource) {
      sessionStorage.setItem('lead_source', utmSource);
    }
  }, [utmSource]);

  useEffect(() => {
    const _linkedin_partner_id = '8934697';
    (window as any)._linkedin_data_partner_ids = (window as any)._linkedin_data_partner_ids || [];
    (window as any)._linkedin_data_partner_ids.push(_linkedin_partner_id);

    (function (l: any) {
      if (!l) {
        (window as any).lintrk = function (a: any, b: any) {
          (window as any).lintrk.q.push([a, b]);
        };
        (window as any).lintrk.q = [];
      }
      const s = document.getElementsByTagName('script')[0];
      const b = document.createElement('script');
      b.type = 'text/javascript';
      b.async = true;
      b.src = 'https://snap.licdn.com/li.lms-analytics/insight.min.js';
      s.parentNode?.insertBefore(b, s);
    })((window as any).lintrk);

    return () => {
      const scripts = document.querySelectorAll(
        'script[src="https://snap.licdn.com/li.lms-analytics/insight.min.js"]',
      );
      scripts.forEach(script => script.remove());
    };
  }, []);

  useEffect(() => {
    const handleCalendlyEvent = async (e: MessageEvent) => {
      if (e.data.event === 'calendly.event_scheduled') {
        setIsBookingCompleted(true);

        const source = sessionStorage.getItem('lead_source') || 'direct';
        const inviteeUri = e.data.payload?.invitee?.uri;

        if (!inviteeUri) {
          console.error('No invitee URI found in Calendly event');
          setUpdateStatus('error');
          return;
        }

        setIsUpdating(true);
        setUpdateStatus(null);

        const apiUrl =
          process.env.REACT_APP_API_URL || 'https://5kdsze9xs4.execute-api.eu-central-1.amazonaws.com';

        const payload = JSON.stringify({ inviteeUri, source });
        const blob = new Blob([payload], { type: 'application/json' });

        const sent = navigator.sendBeacon(`${apiUrl}/update-contact`, blob);

        if (sent) {
          setUpdateStatus('success');
        } else {
          console.error('Failed to send with sendBeacon, trying fetch...');

          fetch(`${apiUrl}/update-contact`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: payload,
            keepalive: true,
          })
            .then(response => response.json())
            .then(data => {
              setUpdateStatus('success');
            })
            .catch(error => {
              console.error('Error updating contact:', error);
              setUpdateStatus('error');
            });
        }

        setIsUpdating(false);

        try {
          const response = await fetch(`${apiUrl}/update-contact`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ inviteeUri, source }),
          });

          const data = await response.json();

          if (response.ok) {
            setUpdateStatus('success');
          } else {
            console.error('Failed to update contact:', data);
            setUpdateStatus('error');
          }
        } catch (error) {
          console.error('Error updating contact:', error);
          setUpdateStatus('error');
        } finally {
          setIsUpdating(false);
          setTimeout(() => setUpdateStatus(null), 5000);
        }
      }
    };

    window.addEventListener('message', handleCalendlyEvent);

    return () => {
      window.removeEventListener('message', handleCalendlyEvent);
    };
  }, []);

  const calendlyUrl = `https://calendly.com/medbioinformatics/disgenet-demo?utm_source=${encodeURIComponent(utmSource)}&utm_medium=website&utm_campaign=demo_booking`;

  return (
    <div className="book-demo-container" style={{ minHeight: '100vh', backgroundColor: '#f8f9fa' }}>
      <Helmet>
        <title>Book a Demo - DISGENET</title>
        <meta name="description" content="Schedule a demo with our team to learn more about DISGENET" />
      </Helmet>

      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-12 col-lg-10">
            <div className="text-center mb-4">
              <h1 className="mb-3">Book a Demo</h1>
              <p className="text-muted">
                Schedule a personalized demo with our team to discover how DISGENET can help your team
              </p>
            </div>

            {isUpdating && (
              <div className="alert alert-info text-center mb-4" role="alert">
                <div className="spinner-border spinner-border-sm me-2" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                Processing your booking...
              </div>
            )}

            {updateStatus === 'success' && (
              <div className="alert alert-success text-center mb-4" role="alert">
                ✓ Your demo has been scheduled successfully!
              </div>
            )}

            {updateStatus === 'error' && (
              <div className="alert alert-warning text-center mb-4" role="alert">
                Your demo has been scheduled, but we couldn&apos;t track the source. Our team will contact you
                soon.
              </div>
            )}

            <div
              className="calendly-wrapper"
              style={{
                backgroundColor: 'white',
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                overflow: 'hidden',
              }}
            >
              <InlineWidget
                url={calendlyUrl}
                styles={{
                  height: '700px',
                  minWidth: '320px',
                }}
                pageSettings={{
                  backgroundColor: 'ffffff',
                  hideEventTypeDetails: false,
                  hideLandingPageDetails: false,
                  primaryColor: '00a2ff',
                  textColor: '4d5055',
                }}
              />
            </div>

            {isBookingCompleted && (
              <div className="text-center mt-4">
                <SharedButton
                  id="brochure"
                  size="xs"
                  onClick={e => {
                    e.preventDefault();
                    navigate('/brochure');
                  }}
                  type="button"
                  className="btn-gradient-pink mx-0 mb-2 px-4"
                  style={{
                    height: '40px',
                    fontSize: '15px',
                    textAlign: 'center',
                    display: 'inline-block',
                  }}
                >
                  Learn more about DISGENET
                </SharedButton>
              </div>
            )}
          </div>
        </div>
      </div>
      {/* LinkedIn Insight Tag noscript fallback */}
      <noscript>
        <img
          height="1"
          width="1"
          style={{ display: 'none' }}
          alt=""
          src="https://px.ads.linkedin.com/collect/?pid=8934697&fmt=gif"
        />
      </noscript>
    </div>
  );
};

export default BookADemo;
